import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useContracts, useUpdateContractStatus, useUpdateVolumeDistributionStatus } from '@/hooks/useContracts';
import { useBusinessUnits } from '@/hooks/useBusinessUnits';
import { Button } from '@/components/ui/button';
import { Plus, Filter, Search, ChevronLeft, ChevronRight, Loader2, Edit } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import type { ContractStatus, Contract, VolumeDistribution } from '@/types';

export default function Contracts() {
  const { user } = useAuth();
  const [selectedBU, setSelectedBU] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 20;

  const isAdmin = user?.role === 'Admin' || false;
  const isManager = user?.role === 'BU Manager' || isAdmin || false;
  const userBusinessUnitId = user?.businessUnitId;

  // Fetch business units for filter dropdown
  const { data: businessUnits } = useBusinessUnits();

  // Fetch contracts with pagination and filters
  const { data: contractsData, isLoading, error } = useContracts({
    pageNumber: currentPage,
    pageSize,
    search: searchQuery || undefined,
    businessUnitId: selectedBU === 'all' ? undefined : parseInt(selectedBU),
  });

  const handleFilterChange = (value: string) => {
    setSelectedBU(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const canUpdateContract = (contract: Contract) => {
    if (isAdmin) return true;
    // Manager can update if contract has VD in their BU
    if (isManager && userBusinessUnitId && contract.volumeDistributions?.some(
      (vd: VolumeDistribution) => vd.businessUnitId === parseInt(userBusinessUnitId)
    )) {
      return true;
    }
    return false;
  };

  if (error) {
    return (
      <Layout>
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Error loading contracts. Please try again later.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Contracts</h1>
            <p className="text-muted-foreground mt-1">
              Manage and track all contracts across business units
            </p>
          </div>
          <Link to="/contracts/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Contract
            </Button>
          </Link>
        </div>

        {/* Filters and Search */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <Select value={selectedBU} onValueChange={handleFilterChange}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Business Unit" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Business Units</SelectItem>
                {businessUnits?.map((bu) => (
                  <SelectItem key={bu.id} value={bu.id.toString()}>
                    {bu.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative w-[300px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search contracts..."
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Contracts Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : !contractsData || contractsData.items.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-border p-12 text-center">
            <p className="text-lg font-medium text-muted-foreground">No contracts found</p>
            <p className="text-sm text-muted-foreground mt-2">
              {searchQuery
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first contract'}
            </p>
          </div>
        ) : (
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contract Name</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Contract Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Total Volume</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="w-[160px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contractsData.items.map((contract) => (
                  <TableRow key={contract.id}>
                    <TableCell className="font-semibold">{contract.contractName}</TableCell>
                    <TableCell className="font-medium">{contract.productName}</TableCell>
                    <TableCell>{contract.supplierName}</TableCell>
                    <TableCell>{new Date(contract.contractDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{new Date(contract.startDate).toLocaleDateString()}</p>
                        <p className="text-muted-foreground">to {new Date(contract.endDate).toLocaleDateString()}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{(contract.totalVolume / 1000).toFixed(1)} tons</TableCell>
                    <TableCell className="capitalize">{contract.contractType}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link to={`/contracts/${contract.id}`}>
                          <Button variant="outline" size="sm">
                            View
                          </Button>
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {contractsData.totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, contractsData.totalCount)} of {contractsData.totalCount} contracts
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={!contractsData.hasPreviousPage}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(contractsData.totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (contractsData.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= contractsData.totalPages - 2) {
                        pageNum = contractsData.totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <Button
                          key={pageNum}
                          variant={currentPage === pageNum ? "default" : "outline"}
                          size="sm"
                          onClick={() => setCurrentPage(pageNum)}
                          className="w-8 h-8 p-0"
                        >
                          {pageNum}
                        </Button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(contractsData.totalPages, prev + 1))}
                    disabled={!contractsData.hasNextPage}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </Layout>
  );
}
